/**
 * resumeExport.js — 3 Premium ATS-Safe Resume Templates
 *
 * Templates:
 *   modern       — Blue accent bar, clean sans-serif, strong visual hierarchy
 *   professional — Dark navy, conservative spacing, recruiter-trusted layout
 *   technical    — Green accent, monospace name, SDET/automation-culture feel
 *
 * ATS guarantees (all 3 templates):
 *   ✓ Single-column layout — no sidebar, no grid, no flexbox in print layer
 *   ✓ Semantic HTML: <header> <main> <section> <h1> <h2> <ul> <li>
 *   ✓ ATS-safe font stack: Arial / Helvetica / sans-serif only
 *   ✓ No emoji, no icons, no SVG, no images
 *   ✓ Table-based date alignment (not flexbox — ATS parsers handle tables)
 *   ✓ Standardised section names (Work Experience, Skills, Education…)
 *   ✓ Text-selectable PDF via browser print dialog
 *   ✓ No letter-spacing on section titles (prevents ATS token splitting)
 */

// ── HTML escape — idempotent ──────────────────────────────────────────────────
const esc = (str) => {
  let val = str;
  if (typeof val === 'object' && val !== null) {
    val = val.text || val.description || val.label || val.title || val.name || val.value || val.summary || val.language || val.proficiency || '';
  }
  const s = String(typeof val === 'symbol' ? '' : (val ?? '')).trim();
  const decoded = s
    .replace(/&amp;/g,  '&').replace(/&lt;/g,  '<').replace(/&gt;/g,  '>')
    .replace(/&quot;/g, '"').replace(/&#x27;/g, "'").replace(/&#x2F;/g, '/');
  return decoded
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/[\u{1F000}-\u{1FFFF}]/gu, '')
    .replace(/[\u2600-\u27BF]/g, '')
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '');
};

// ── Helper to extract bullet text ─────────────────────────────────────────────
const getBulletStr = (b) => {
  if (!b) return '';
  if (typeof b === 'object' && b !== null) {
    return (b.text || b.description || b.label || b.title || b.value || b.summary || '').trim();
  }
  return typeof b === 'symbol' ? '' : String(b).trim();
};

// ── URL sanitiser ─────────────────────────────────────────────────────────────
const safeUrl = (url) => {
  let val = url;
  if (typeof val === 'object' && val !== null) {
    val = val.url || val.href || val.link || val.value || '';
  }
  const s = String(typeof val === 'symbol' ? '' : (val ?? '')).trim();
  if (!s) return '';
  const l = s.toLowerCase();
  if (l.startsWith('javascript:') || l.startsWith('data:') || l.startsWith('vbscript:')) return '';
  return s;
};

// ── Section heading ───────────────────────────────────────────────────────────
const secTitle = (label) =>
  `<h2 class="sec" aria-label="${esc(label)}">${esc(label)}</h2>`;

// ── Skills row ────────────────────────────────────────────────────────────────
const skillsLine = (label, items = []) => {
  const clean = (Array.isArray(items) ? items : []).filter(Boolean).map(esc).filter(Boolean);
  if (!clean.length) return '';
  return `<p class="sk-row"><strong>${esc(label)}:</strong> ${clean.join(', ')}</p>`;
};

// ── Entry block — table layout for date alignment ─────────────────────────────
const entryBlock = ({ title, subtitle, date, description = '', bullets = [], extra = '', afterBullets = '' }) => {
  const dateStr = getBulletStr(date);
  const hasDate = Boolean(dateStr);
  const headerRow = hasDate
    ? `<table class="entry-hdr" role="presentation"><tr>
         <td class="entry-title">${esc(title)}</td>
         <td class="entry-date">${esc(dateStr)}</td>
       </tr></table>`
    : `<p class="entry-title-solo">${esc(title)}</p>`;
  const descStr = getBulletStr(description);
  const validBullets = (Array.isArray(bullets) ? bullets : []).map(getBulletStr).filter(Boolean);
  return `
<div class="entry">
  ${headerRow}
  ${subtitle ? `<p class="entry-sub">${esc(subtitle)}</p>` : ''}
  ${extra}
  ${descStr ? `<p class="entry-desc">${esc(descStr)}</p>` : ''}
  ${validBullets.length ? `<ul>${validBullets.map(b => `<li>${esc(b)}</li>`).join('')}</ul>` : ''}
  ${afterBullets}
</div>`;
};

const buildContactLine = (p) =>
  [
    p.email    ? esc(p.email)                               : '',
    p.phone    ? esc(p.phone)                               : '',
    p.location ? esc(p.location)                            : '',
    p.linkedin ? `LinkedIn: ${esc(safeUrl(p.linkedin))}`    : '',
    p.github   ? `GitHub: ${esc(safeUrl(p.github))}`        : '',
    p.portfolio? `Portfolio: ${esc(safeUrl(p.portfolio))}`  : '',
  ].filter(Boolean).join(' &bull; ');

// ── Section builders ──────────────────────────────────────────────────────────
const buildSummary = (fd) => {
  const t = fd?.summary?.trim();
  if (!t) return '';
  return `<section aria-label="Professional Summary">${secTitle('Professional Summary')}<p class="summary">${esc(t)}</p></section>`;
};

const buildSkills = (fd) => {
  const sk = fd?.technicalSkills || {};
  const tl = fd?.tools           || {};
  const rows = [
    skillsLine('Core Competencies', fd?.coreCompetencies || []),
    skillsLine('Testing',           sk.testing        || []),
    skillsLine('Automation',        sk.automation     || []),
    skillsLine('Programming',       sk.programming    || []),
    skillsLine('Databases',         sk.databases      || []),
    skillsLine('Cloud Platforms',   sk.cloud          || []),
    skillsLine('AI & ML Tools',     sk.aiTools        || []),
    skillsLine('Test Management',   tl.testManagement || []),
    skillsLine('CI/CD',             tl.cicd           || []),
    skillsLine('Monitoring',        tl.monitoring     || []),
  ].filter(Boolean).join('\n');
  if (!rows) return '';
  return `<section aria-label="Skills">${secTitle('Skills')}${rows}</section>`;
};

const buildExperience = (fd) => {
  const entries = (fd?.experience || [])
    .filter(e => e.company?.trim() || e.role?.trim())
    .map(e => {
      const techClean = (e.technologies || '').trim();
      const metaRow = techClean ? `<div class="meta-row"><span class="meta-label">Technologies:</span> <span class="meta-val">${esc(techClean)}</span></div>` : '';
      const impactRow = e.impact ? `<div class="impact-block"><strong class="impact-label">Key Impact:</strong> <span class="impact-val">${esc(e.impact)}</span></div>` : '';
      return entryBlock({
        title:   e.role    || '',
        subtitle:[e.company, e.location].filter(Boolean).join(', '),
        date:    e.duration || '',
        extra:   metaRow + impactRow,
        bullets: (e.achievements || []).map(a => (a && typeof a === 'object' ? a.text : a)).filter(Boolean),
      });
    }).join('');
  if (!entries) return '';
  return `<section aria-label="Work Experience">${secTitle('Work Experience')}${entries}</section>`;
};

export const getResponsibilityBullets = (p) => {
  if (!p || !p.responsibilities) return [];
  if (Array.isArray(p.responsibilities)) {
    return p.responsibilities
      .map(r => (typeof r === 'string' ? r : r?.text || ''))
      .filter(t => t && t.trim());
  }
  if (typeof p.responsibilities === 'string') {
    return p.responsibilities
      .split('\n')
      .map(t => t.trim())
      .filter(Boolean);
  }
  return [];
};

export const formatContactLink = (url, type = '') => {
  if (!url) return '';
  let str = String(url).trim().replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
  
  if (type === 'linkedin') {
    const match = str.match(/linkedin\.com\/in\/([^/]+)/) || str.match(/in\/([^/]+)/);
    const username = match && match[1] ? match[1] : str.replace(/^linkedin\.com\//, '').replace(/^in\//, '');
    return `linkedin.com/in/${username}`;
  }
  
  if (type === 'github') {
    const username = str.replace(/^github\.com\//, '');
    return `github.com/${username}`;
  }

  return str;
};

export const formatProjectMetadata = (p) => {
  if (!p) return { cleanTitle: '', domainLine: '', techLine: '', domainClean: '', techClean: '' };

  const cleanTitle = (p.title || '')
    .replace(/\s+,\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();

  const domainClean = (p.domain || '')
    .split(/[|•]/)
    .map(s => s.trim())
    .filter(Boolean)
    .join(' • ');

  const techClean = (p.technologies || '')
    .replace(/\s+,\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();

  const domainLine = domainClean ? `Domain: ${domainClean}` : '';
  const techLine = techClean ? `Technologies: ${techClean}` : '';

  return {
    cleanTitle,
    domainLine,
    techLine,
    domainClean,
    techClean
  };
};

const buildProjects = (fd) => {
  const entries = (fd?.projects || [])
    .filter(p => p.title?.trim())
    .map(p => {
      const { cleanTitle, domainClean, techClean } = formatProjectMetadata(p);
      const respBullets = getResponsibilityBullets(p);
      const metaRows = [
        domainClean ? `<div class="meta-row"><span class="meta-label">Domain:</span> <span class="meta-val">${esc(domainClean)}</span></div>` : '',
        techClean ? `<div class="meta-row"><span class="meta-label">Technologies:</span> <span class="meta-val">${esc(techClean)}</span></div>` : ''
      ].filter(Boolean).join('');
      const impactRow = p.impact ? `<div class="impact-block"><strong class="impact-label">Key Impact:</strong> <span class="impact-val">${esc(p.impact)}</span></div>` : '';

      return entryBlock({
        title:        cleanTitle,
        subtitle:     '',
        date:         '',
        description:  p.description,
        extra:        metaRows,
        bullets:      respBullets,
        afterBullets: impactRow,
      });
    }).join('');
  if (!entries) return '';
  return `<section aria-label="Projects">${secTitle('Projects')}${entries}</section>`;
};

const buildCertifications = (fd) => {
  const entries = (fd?.certifications || [])
    .filter(c => c.name?.trim())
    .map(c => entryBlock({
      title:   c.name,
      subtitle:[c.organization, c.credentialId ? `ID: ${c.credentialId}` : ''].filter(Boolean).join(' — '),
      date:    c.year || '',
      bullets: [],
    })).join('');
  if (!entries) return '';
  return `<section aria-label="Certifications">${secTitle('Certifications')}${entries}</section>`;
};

const buildEducation = (fd) => {
  const entries = (fd?.education || [])
    .filter(e => e.degree?.trim() || e.university?.trim())
    .map(e => entryBlock({
      title:   e.degree || '',
      subtitle:[e.university, e.gpa ? `GPA: ${e.gpa}` : ''].filter(Boolean).join(' — '),
      date:    e.year || '',
      bullets: [],
    })).join('');
  if (!entries) return '';
  return `<section aria-label="Education">${secTitle('Education')}${entries}</section>`;
};

const buildOptional = (fd, sectionsEnabled = {}) => {
  const parts = [];

  // Achievements
  const achEntries = (fd?.achievements || []).filter(a => a.title?.trim())
    .map(a => entryBlock({ title: a.title, subtitle: [a.organization, a.year].filter(Boolean).join(', '), date: '', bullets: a.description ? [a.description] : [] })).join('');
  if (achEntries) parts.push(`<section aria-label="Key Achievements">${secTitle('Key Achievements')}${achEntries}</section>`);

  if (sectionsEnabled.awards) {
    const e = (fd?.awards || []).filter(a => a.title?.trim())
      .map(a => entryBlock({ title: a.title, subtitle: [a.organization, a.year].filter(Boolean).join(', '), date: '', bullets: a.description ? [a.description] : [] })).join('');
    if (e) parts.push(`<section aria-label="Awards and Recognition">${secTitle('Awards and Recognition')}${e}</section>`);
  }

  if (sectionsEnabled.publications) {
    const e = (fd?.publications || []).filter(p => p.title?.trim())
      .map(p => entryBlock({ title: p.title, subtitle: [p.publication, p.type, p.date].filter(Boolean).join(' — '), date: '', bullets: p.url ? [`URL: ${safeUrl(p.url)}`] : [] })).join('');
    if (e) parts.push(`<section aria-label="Publications and Talks">${secTitle('Publications and Talks')}${e}</section>`);
  }

  if (sectionsEnabled.openSource) {
    const e = (fd?.openSource || []).filter(o => o.project?.trim())
      .map(o => entryBlock({ title: o.project, subtitle: [o.role, o.technologies].filter(Boolean).join(' — '), date: '', bullets: [o.description, o.stars ? `Stars: ${o.stars}` : '', o.url ? `URL: ${safeUrl(o.url)}` : ''].filter(Boolean) })).join('');
    if (e) parts.push(`<section aria-label="Open Source Contributions">${secTitle('Open Source Contributions')}${e}</section>`);
  }

  if (sectionsEnabled.memberships) {
    const e = (fd?.memberships || []).filter(m => m.organization?.trim())
      .map(m => entryBlock({ title: m.organization, subtitle: [m.role, m.level].filter(Boolean).join(', '), date: m.since ? `Since ${m.since}` : '', bullets: [] })).join('');
    if (e) parts.push(`<section aria-label="Professional Memberships">${secTitle('Professional Memberships')}${e}</section>`);
  }

  if (sectionsEnabled.volunteer) {
    const e = (fd?.volunteer || []).filter(v => v.organization?.trim())
      .map(v => entryBlock({ title: v.role || '', subtitle: v.organization, date: v.duration || '', bullets: v.description ? [v.description] : [] })).join('');
    if (e) parts.push(`<section aria-label="Volunteer Experience">${secTitle('Volunteer Experience')}${e}</section>`);
  }

  if (sectionsEnabled.languages) {
    const line = (fd?.languages || []).filter(l => l.language?.trim())
      .map(l => `${esc(l.language)} (${esc(l.proficiency)})`).join(', ');
    if (line) parts.push(`<section aria-label="Languages">${secTitle('Languages')}<p class="sk-row">${line}</p></section>`);
  }

  return parts.join('\n');
};

// ══════════════════════════════════════════════════════════════════════════════
// TEMPLATE CSS — 3 distinct premium themes
// ══════════════════════════════════════════════════════════════════════════════

const BASE_CSS = (vars) => `
  /* ── Reset ── */
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  /* ── Base ── */
  body {
    font-family: ${vars.bodyFont};
    font-size: ${vars.baseFontSize};
    line-height: ${vars.lineHeight};
    color: ${vars.textColor};
    background: #ffffff;
  }

  /* ── Page wrapper ── */
  .page {
    max-width: 760px;
    margin: 0 auto;
    padding: ${vars.pagePadding};
  }

  /* ── Header ── */
  header {
    ${vars.headerStyle}
    margin-bottom: ${vars.headerMarginBottom};
    padding-bottom: ${vars.headerPaddingBottom};
  }
  h1 {
    font-family: ${vars.nameFont};
    font-size: ${vars.nameFontSize};
    font-weight: 700;
    color: ${vars.nameColor};
    line-height: 1.15;
    letter-spacing: ${vars.nameLetterSpacing};
  }
  .headline {
    font-size: ${vars.headlineFontSize};
    color: ${vars.headlineColor};
    font-weight: 600;
    margin-top: 3px;
  }
  .target-headline {
    font-size: 9.5pt;
    font-weight: 700;
    color: ${vars.sectionColor || '#0057B7'};
    margin-top: 2px;
  }
  .contact {
    font-size: 8pt;
    color: ${vars.contactColor};
    margin-top: 5px;
    line-height: 1.7;
    word-break: break-word;
  }

  /* ── Section titles ── */
  .sec {
    font-family: ${vars.sectionFont};
    font-size: ${vars.sectionFontSize};
    font-weight: 700;
    text-transform: uppercase;
    color: ${vars.sectionColor};
    ${vars.sectionBorderStyle}
    padding-bottom: 3px;
    margin: ${vars.sectionMarginTop} 0 6px;
  }

  /* ── Summary ── */
  .summary {
    font-size: 9.5pt;
    color: #374151;
    line-height: 1.65;
  }

  /* ── Skills rows ── */
  .sk-row {
    font-size: 9pt;
    color: #374151;
    margin-bottom: 3px;
    line-height: 1.55;
  }
  .sk-row strong { color: #111827; font-weight: 700; }

  /* ── Entry blocks ── */
  .entry { margin-bottom: ${vars.entryMarginBottom}; }

  .entry-hdr {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  .entry-title {
    font-size: ${vars.entryTitleSize};
    font-weight: 700;
    color: ${vars.entryTitleColor};
    width: 74%;
    vertical-align: top;
  }
  .entry-date {
    font-size: 8.5pt;
    color: #6b7280;
    text-align: right;
    white-space: nowrap;
    width: 26%;
    vertical-align: top;
  }
  .entry-title-solo {
    font-size: ${vars.entryTitleSize};
    font-weight: 700;
    color: ${vars.entryTitleColor};
  }
  .impact-block {
    background: #f0f9ff;
    border-left: 3px solid ${vars.sectionColor || '#0ea5e9'};
    padding: 4px 8px;
    margin: 4px 0 6px;
    border-radius: 3px;
    font-size: 9pt;
  }
  .impact-label {
    font-weight: 700;
    color: ${vars.sectionColor || '#0ea5e9'};
  }
  .impact-val {
    color: #1e293b;
  }
  .entry-sub {
    font-size: 9pt;
    color: ${vars.entrySubColor};
    margin: 2px 0 4px;
    font-style: italic;
  }
  .meta-row {
    font-size: 9pt;
    color: #4b5563;
    margin: 2px 0 3px;
    line-height: 1.45;
  }
  .meta-label {
    font-weight: 700;
    color: #111827;
  }
  .meta-val {
    color: #374151;
  }

  /* ── Bullet lists ── */
  ul { padding-left: 14px; margin-top: 4px; }
  li {
    font-size: 9.5pt;
    color: #374151;
    margin-bottom: 2px;
    line-height: 1.55;
    ${vars.listStyleType ? `list-style-type: "${vars.listStyleType}";` : ''}
  }

  /* ── Page numbers ── */
  @page {
    margin: 0.5cm 0.6cm;
    size: A4 portrait;
    @bottom-right {
      content: "Page " counter(page) " of " counter(pages);
      font-family: Arial, Helvetica, sans-serif;
      font-size: 7pt;
      color: #9ca3af;
    }
  }

  /* ── Print ── */
  @media print {
    body  { font-size: 10pt; }
    .page { padding: 0; max-width: 100%; }
    .sec        { page-break-after: avoid; }
    .entry      { page-break-inside: avoid; }
    header      { page-break-after: avoid; }
    section     { page-break-inside: avoid; }
    *           { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

// ── Template 1: Modern ────────────────────────────────────────────────────────
// Blue accent bar under name, clean sans-serif, strong visual hierarchy.
// Feels like a 2026 tech company resume — confident, readable, modern.
const MODERN_CSS = BASE_CSS({
  bodyFont:            'Arial, Helvetica, sans-serif',
  nameFont:            'Arial, Helvetica, sans-serif',
  sectionFont:         'Arial, Helvetica, sans-serif',
  baseFontSize:        '10.5pt',
  lineHeight:          '1.55',
  textColor:           '#1e293b',
  pagePadding:         '28px 36px',

  // Header: left-aligned name with a bold blue bottom border
  nameColor:           '#0057B7',
  nameFontSize:        '20pt',
  nameLetterSpacing:   '-0.3px',
  headlineFontSize:    '10.5pt',
  headlineColor:       '#334155',
  contactColor:        '#475569',
  headerStyle:         'border-bottom: 3px solid #0057B7;',
  headerMarginBottom:  '14px',
  headerPaddingBottom: '10px',

  // Sections: blue text, thin blue underline
  sectionColor:        '#0057B7',
  sectionFontSize:     '8.5pt',
  sectionBorderStyle:  'border-bottom: 1.5px solid #bfdbfe;',
  sectionMarginTop:    '14px',

  // Entries
  entryTitleSize:      '10.5pt',
  entryTitleColor:     '#0f172a',
  entrySubColor:       '#475569',
  entryMarginBottom:   '10px',
  listStyleType:       '',
});

// ── Template 2: Professional ──────────────────────────────────────────────────
// Dark navy, conservative double-rule header, classic proportions.
// The "safe choice" — trusted by Fortune 500 recruiters and ATS systems alike.
const PROFESSIONAL_CSS = BASE_CSS({
  bodyFont:            'Arial, Helvetica, sans-serif',
  nameFont:            'Arial, Helvetica, sans-serif',
  sectionFont:         'Arial, Helvetica, sans-serif',
  baseFontSize:        '10.5pt',
  lineHeight:          '1.6',
  textColor:           '#111827',
  pagePadding:         '32px 40px',

  // Header: centered name, double-rule border
  nameColor:           '#1a1a2e',
  nameFontSize:        '22pt',
  nameLetterSpacing:   '0px',
  headlineFontSize:    '10pt',
  headlineColor:       '#374151',
  contactColor:        '#4b5563',
  headerStyle:         `
    text-align: center;
    border-top: 2px solid #1a1a2e;
    border-bottom: 2px solid #1a1a2e;
    padding-top: 8px;
  `,
  headerMarginBottom:  '16px',
  headerPaddingBottom: '8px',

  // Sections: dark navy, full-width solid rule
  sectionColor:        '#1a1a2e',
  sectionFontSize:     '9pt',
  sectionBorderStyle:  'border-bottom: 1px solid #1a1a2e;',
  sectionMarginTop:    '16px',

  // Entries
  entryTitleSize:      '10.5pt',
  entryTitleColor:     '#111827',
  entrySubColor:       '#4b5563',
  entryMarginBottom:   '11px',
  listStyleType:       '',
});

// ── Template 3: Technical ─────────────────────────────────────────────────────
// Emerald green accent, monospace name, left-border section markers.
// Signals engineering culture — perfect for SDET, Automation, AI QA roles.
const TECHNICAL_CSS = BASE_CSS({
  bodyFont:            'Arial, Helvetica, sans-serif',
  nameFont:            '"Courier New", Courier, monospace',
  sectionFont:         'Arial, Helvetica, sans-serif',
  baseFontSize:        '10pt',
  lineHeight:          '1.55',
  textColor:           '#1f2937',
  pagePadding:         '28px 36px',

  // Header: monospace name, green left border accent
  nameColor:           '#064e3b',
  nameFontSize:        '18pt',
  nameLetterSpacing:   '-0.5px',
  headlineFontSize:    '10pt',
  headlineColor:       '#374151',
  contactColor:        '#4b5563',
  headerStyle:         `
    border-left: 4px solid #059669;
    padding-left: 12px;
    border-bottom: 1px solid #d1fae5;
  `,
  headerMarginBottom:  '14px',
  headerPaddingBottom: '10px',

  // Sections: green text, left-border marker
  sectionColor:        '#065f46',
  sectionFontSize:     '8.5pt',
  sectionBorderStyle:  'border-bottom: 1.5px solid #6ee7b7;',
  sectionMarginTop:    '14px',

  // Entries
  entryTitleSize:      '10pt',
  entryTitleColor:     '#111827',
  entrySubColor:       '#4b5563',
  entryMarginBottom:   '10px',
  listStyleType:       '> ',
}) + `
  /* Technical-only: monospace for entry titles to reinforce code culture */
  .entry-title,
  .entry-title-solo {
    font-family: "Courier New", Courier, monospace;
    font-size: 10pt;
  }
  /* Subtle code-block feel for skills rows */
  .sk-row {
    font-family: "Courier New", Courier, monospace;
    font-size: 8.5pt;
    background: #f0fdf4;
    padding: 2px 4px;
    border-radius: 2px;
    margin-bottom: 4px;
  }
  .sk-row strong { color: #065f46; }
`;

const TEMPLATE_CSS = {
  modern:       MODERN_CSS,
  professional: PROFESSIONAL_CSS,
  technical:    TECHNICAL_CSS,
};

// ── Main HTML builder ─────────────────────────────────────────────────────────
export const buildResumeHTML = (formData, template = 'modern', sectionsEnabled = {}) => {
  const p   = formData?.personal || {};
  const css = TEMPLATE_CSS[template] || TEMPLATE_CSS.modern;
  const contact = buildContactLine(p);

  // Professional template centers the header — add inline style to h1/headline
  const headerCenter = template === 'professional'
    ? ' style="text-align:center;"'
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${esc(p.name || 'Resume')} — QA Resume</title>
<style>${css}</style>
</head>
<body>
<div class="page">

  <header>
    <h1${headerCenter}>${esc(p.name || 'Your Name')}</h1>
    <p class="headline"${headerCenter}>${esc(p.jobTitle || 'QA Engineer')}</p>
    ${p.targetHeadline ? `<p class="target-headline"${headerCenter}>${esc(p.targetHeadline)}</p>` : ''}
    ${contact ? `<p class="contact"${headerCenter}>${contact}</p>` : ''}
  </header>

  <main>
    ${buildSummary(formData)}
    ${buildSkills(formData)}
    ${buildExperience(formData)}
    ${buildProjects(formData)}
    ${buildCertifications(formData)}
    ${buildEducation(formData)}
    ${buildOptional(formData, sectionsEnabled)}
  </main>

</div>
</body>
</html>`;
};

// ── PDF Download — browser print dialog → real text-based PDF ────────────────
export const downloadResumePDF = async (formData, template = 'modern', sectionsEnabled = {}) => {
  const html     = buildResumeHTML(formData, template, sectionsEnabled);
  const safeName = (formData?.personal?.name || 'Resume').trim().replace(/\s+/g, '_');
  const fileName = `QA_Resume_${safeName}`;

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    // Popup blocked — fall back to HTML blob download
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${fileName}.html`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    throw new Error('Popup blocked. HTML file downloaded — open in browser and use File › Print › Save as PDF.');
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  printWindow.addEventListener('load', () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 1000);
    }, 400);
  });
};

// ── Preview in new tab ────────────────────────────────────────────────────────
export const previewResume = (formData, template = 'modern', sectionsEnabled = {}) => {
  const html = buildResumeHTML(formData, template, sectionsEnabled);
  const win  = window.open('', '_blank');
  if (!win) return;
  win.document.open(); win.document.write(html); win.document.close();
};

// ── Print ─────────────────────────────────────────────────────────────────────
export const printResume = (formData, template = 'modern', sectionsEnabled = {}) => {
  const html = buildResumeHTML(formData, template, sectionsEnabled);
  const win  = window.open('', '_blank');
  if (!win) return;
  win.document.open(); win.document.write(html); win.document.close();
  win.addEventListener('load', () => setTimeout(() => { win.focus(); win.print(); }, 300));
};

// ── Legacy compat ─────────────────────────────────────────────────────────────
export const downloadHTML   = downloadResumePDF;
export const downloadResume = downloadResumePDF;
